import { useEffect, useState } from 'react';
import { ArrowPathIcon, ExclamationTriangleIcon, UsersIcon } from '@heroicons/react/24/outline';
import { mandantNutzer, type MandantNutzer, type MandantNutzerAntwort } from '../../api/unimedica';

/* Benutzer eines Mandanten in der Betreiber-Sicht.
 *
 * Die Praxis selbst verwaltet ihre Leute weiter unter Verwaltung → Nutzer. Was hier dazukommt,
 * ist der Abgleich mit PowerID: ob sich jemand ueberhaupt noch anmelden kann. Diese Wahrheit
 * liegt nicht in der Mandanten-Datenbank — dort steht ein Konto genauso da, ob die Person
 * laengst aus der Zugangsgruppe geflogen ist oder taeglich arbeitet.
 */

function Merkmal({ an, text, aus }: { an: boolean | null | undefined; text: string; aus?: string }) {
  if (an === null || an === undefined) return <span className="text-ps-dark-300">·</span>;
  return (
    <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[11px] ${
      an ? 'bg-emerald-50 text-emerald-800' : 'bg-rose-50 text-rose-700'}`}>
      {an ? text : (aus ?? `kein ${text}`)}
    </span>
  );
}

const ROLLE: Record<string, string> = {
  admin: 'Admin', arzt: 'Arzt', mpa: 'MPA', pflege: 'Pflege', user: 'Nutzer',
};

export default function NutzerTab({ slug, flash }: { slug: string; flash: (m: string, v?: 'ok' | 'err') => void }) {
  const [daten, setDaten] = useState<MandantNutzerAntwort | null>(null);
  const [laedt, setLaedt] = useState(false);

  const laden = async () => {
    setLaedt(true);
    try { setDaten(await mandantNutzer(slug)); }
    catch (e: any) { flash(e?.message ?? String(e), 'err'); }
    finally { setLaedt(false); }
  };
  useEffect(() => { void laden(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [slug]);

  const nutzer: MandantNutzer[] = daten?.nutzer ?? [];
  const mitHinweis = nutzer.filter((n) => n.hinweise.length > 0).length;

  return (
    <div className="space-y-3 border-t border-gray-100 pt-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-1.5 text-sm font-semibold text-ps-dark-800">
            <UsersIcon className="h-4 w-4 text-ps-dark-400" /> Benutzer dieser Praxis
          </div>
          <p className="text-xs text-ps-dark-400">
            Angelegt und gepflegt wird in der Praxis selbst (Verwaltung → Nutzer). Hier steht daneben,
            was PowerID über dieselbe Person sagt — nur dort entscheidet sich, wer sich anmelden kann.
          </p>
        </div>
        <button onClick={() => void laden()} disabled={laedt} className="btn-ghost shrink-0 text-xs">
          <ArrowPathIcon className={`mr-1 inline h-3.5 w-3.5 ${laedt ? 'animate-spin' : ''}`} />
          {laedt ? 'Lade …' : 'Neu abgleichen'}
        </button>
      </div>

      {daten && !daten.powerid.erreichbar && (
        <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          PowerID wurde nicht erreicht ({daten.powerid.grund ?? 'unbekannter Grund'}). Unten stehen
          nur die Konten dieser Praxis — ob sich jemand anmelden kann, lässt sich gerade nicht sagen.
        </div>
      )}

      {daten?.powerid.erreichbar && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-ps-dark-500">
          <span>Zugang: <code className="text-ps-dark-700">{daten.gruppen.zugang}</code>
            {!daten.gruppen.zugang_da && <span className="ml-1 text-rose-700">(Gruppe fehlt in PowerID)</span>}</span>
          <span>Praxis-Admin: <code className="text-ps-dark-700">{daten.gruppen.admin}</code>
            {!daten.gruppen.admin_da && <span className="ml-1 text-rose-700">(Gruppe fehlt in PowerID)</span>}</span>
          {mitHinweis > 0 && (
            <span className="inline-flex items-center gap-1 text-amber-800">
              <ExclamationTriangleIcon className="h-3.5 w-3.5" />
              {mitHinweis} {mitHinweis === 1 ? 'Eintrag braucht' : 'Einträge brauchen'} einen Blick
            </span>
          )}
        </div>
      )}

      <div className="overflow-x-auto rounded border border-gray-200">
        <table className="w-full min-w-[46rem] text-sm">
          <thead>
            <tr className="bg-gray-50 text-left text-[11px] uppercase tracking-wide text-ps-dark-500">
              <th className="px-3 py-2 font-medium">Person</th>
              <th className="px-3 py-2 font-medium">Rolle hier</th>
              <th className="px-3 py-2 font-medium">In PowerID</th>
              <th className="px-3 py-2 font-medium">Zuletzt angemeldet</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {nutzer.length === 0 && !laedt && (
              <tr><td colSpan={4} className="px-3 py-4 text-center text-xs text-ps-dark-400">
                Noch niemand angelegt.
              </td></tr>
            )}
            {nutzer.map((n) => (
              <tr key={n.id ?? n.username} className={n.hinweise.length ? 'bg-amber-50/40' : ''}>
                <td className="px-3 py-2 align-top">
                  <div className="font-medium text-ps-dark-900">{n.name || n.username}</div>
                  <div className="text-[11px] text-ps-dark-400">{n.email}</div>
                  {n.hinweise.map((h, i) => (
                    <div key={i} className="mt-1 flex items-start gap-1 text-[11px] text-amber-900">
                      <ExclamationTriangleIcon className="mt-px h-3 w-3 shrink-0" />
                      <span>{h}</span>
                    </div>
                  ))}
                </td>
                <td className="whitespace-nowrap px-3 py-2 align-top">
                  {n.rolle ? (
                    <>
                      <span className="text-ps-dark-800">{ROLLE[n.rolle] ?? n.rolle}</span>
                      {n.aktiv === false && <div className="text-[11px] text-rose-700">hier deaktiviert</div>}
                    </>
                  ) : <span className="text-[11px] text-ps-dark-400">noch kein Konto</span>}
                </td>
                <td className="px-3 py-2 align-top">
                  {n.powerid ? (
                    <div className="flex flex-wrap gap-1">
                      <Merkmal an={n.powerid.vorhanden} text="Konto" aus="kein Konto" />
                      {n.powerid.vorhanden && <Merkmal an={n.powerid.aktiv} text="aktiv" aus="gesperrt" />}
                      {n.powerid.vorhanden && <Merkmal an={n.powerid.in_zugang} text="Zugang" aus="kein Zugang" />}
                      {n.powerid.in_admin && <span className="inline-flex items-center rounded bg-primary-50 px-1.5 py-0.5 text-[11px] text-primary-700">Praxis-Admin</span>}
                    </div>
                  ) : <span className="text-[11px] text-ps-dark-300">nicht abgefragt</span>}
                </td>
                <td className="whitespace-nowrap px-3 py-2 align-top text-ps-dark-600">
                  {n.letzter_login
                    ? new Date(n.letzter_login).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })
                    : <span className="text-[11px] text-ps-dark-400">nie</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-ps-dark-400">
        Ändern lässt sich beides nicht von hier: Rollen in der Praxis-Oberfläche, Gruppen in PowerID.
        Das ist Absicht — ein Betreiber soll sehen, was los ist, ohne in den Praxisbetrieb zu greifen.
      </p>
    </div>
  );
}
